import React, { useEffect, useRef, useState } from 'react';
import { useJarvisContext } from '../../context/JarvisContext';

// Small bottom banner that surfaces the browser's native install prompt
// (beforeinstallprompt) with an explicit button, instead of relying on
// Chrome's own address-bar icon which people don't notice.
//
// beforeinstallprompt is NOT reliable on its own: Chrome gates it behind
// its own "engagement heuristics" (roughly: has the service worker
// actually taken control yet — which needs a page load AFTER the first
// one that registered it — plus some minimum time-on-site/return-visit
// signal). Reported live: it can legitimately not fire for a while even
// with every technical installability requirement met. So this also
// falls back to manual step-by-step instructions after a short wait, so
// there's always SOME visible way to install, not silence.
const IS_ELECTRON = !!(window.electronAPI && window.electronAPI.isElectron);

const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const IS_IOS = /iPad|iPhone|iPod/.test(UA) && !window.MSStream;
const IS_ANDROID = /Android/.test(UA);

function manualSteps() {
  if (IS_IOS) {
    return { icon: '⬆️', text: 'खालच्या Share बटणावर (⬆️) टॅप करा → "Add to Home Screen" निवडा' };
  }
  if (IS_ANDROID) {
    return { icon: '⋮', text: 'वरच्या उजव्या ⋮ मेनूवर टॅप करा → "Install app" किंवा "Add to Home screen" निवडा' };
  }
  return { icon: '⋮', text: 'ब्राउझरच्या ⋮ मेनूमध्ये "Install JARVIS AI…" शोधा' };
}

export default function InstallPWA() {
  const { state } = useJarvisContext();
  const onChatScreen = state.activeScreen === 'v5chat';
  // Also suppressed while the mobile nav drawer is open — this banner is
  // a sibling of the drawer, not nested inside it, so it can render on
  // top of the drawer's own nav items and block clicks on them (the same
  // stacking-context issue BottomBar had — see V5Shell.jsx's note).
  const drawerOpen = state.sidebarOpen;
  const [promptEvent, setPromptEvent] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('jarvis_install_dismissed') === '1'; } catch { return false; }
  });
  const [installed, setInstalled] = useState(
    () => window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
  );

  // Mirrors promptEvent so the delayed timeout below can read the LATEST
  // value instead of the one closed over when the effect first ran (a
  // plain read of the promptEvent state var inside that setTimeout would
  // always see its initial null, permanently, regardless of what
  // setPromptEvent was called with afterwards — state updates don't
  // reach into an already-created closure).
  const promptEventRef = useRef(null);

  useEffect(() => {
    if (IS_ELECTRON) return;
    const onPrompt = (e) => { e.preventDefault(); promptEventRef.current = e; setPromptEvent(e); };
    const onInstalled = () => { setInstalled(true); promptEventRef.current = null; setPromptEvent(null); setShowManual(false); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // Fallback: if Chrome's own prompt hasn't fired after a few seconds,
    // show manual instructions instead of leaving the user with nothing.
    // Skipped entirely on desktop non-mobile browsers with no UA match —
    // there the address-bar install icon is reasonably discoverable and
    // generic "⋮ menu" instructions are more likely to be wrong than
    // helpful.
    const t = setTimeout(() => {
      if (!promptEventRef.current && (IS_IOS || IS_ANDROID)) setShowManual(true);
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(t);
    };
  }, []);

  if (IS_ELECTRON || installed || dismissed || onChatScreen || drawerOpen || (!promptEvent && !showManual)) return null;

  const handleInstall = async () => {
    promptEvent.prompt();
    try {
      const { outcome } = await promptEvent.userChoice;
      if (outcome === 'accepted') setInstalled(true);
    } catch { /* ignore */ }
    setPromptEvent(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('jarvis_install_dismissed', '1'); } catch { /* ignore */ }
  };

  const steps = manualSteps();

  return (
    <div style={{
      // bottom:100px, not 12px — V5Shell's BottomBar (the "Ask JARVIS"
      // chat input) is also fixed to the bottom of the screen; sitting
      // this low would overlap it instead of stacking above it.
      position: 'fixed', left: '12px', right: '12px', bottom: '100px', zIndex: 1500,
      background: '#111827', border: '1px solid #00d4ff', borderRadius: '10px',
      padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px',
      fontFamily: 'monospace', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    }}>
      <span style={{ fontSize: '20px' }}>{promptEvent ? '📲' : steps.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '700' }}>Install JARVIS App</div>
        <div style={{ color: '#94a3b8', fontSize: '11px' }}>
          {promptEvent ? 'Home screen वर add करा — app सारखं उघडेल' : steps.text}
        </div>
      </div>
      {promptEvent && (
        <button onClick={handleInstall} style={{
          background: '#00d4ff', color: '#000', border: 'none', borderRadius: '6px',
          padding: '8px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
          fontFamily: 'monospace', flexShrink: 0,
        }}>Install</button>
      )}
      <button onClick={handleDismiss} aria-label="Dismiss" style={{
        background: 'transparent', color: '#94a3b8', border: 'none', fontSize: '16px',
        cursor: 'pointer', padding: '4px', flexShrink: 0,
      }}>✕</button>
    </div>
  );
}
